import { FileUploadState } from './FileUploadState';
import { getFileExtensionIconClass } from './utils';
import { useEffect } from 'react';
import { GdkAlert } from '@gdk/alert';
import { classes } from '../../util/style-utils';

export default function FileUploadFileSelection({
    setErrorMessage,
    selectedFiles,
    setSelectedFiles = () => { },
    fileUploadState,
    setFileUploadState = () => { },
    fileUploadParameters,
    displayParameters
}) {
    useEffect(() => {
        if (fileUploadState === FileUploadState.Error) {
            new GdkAlert({ content: "#file-upload-error-alert" });
        }
    }, [fileUploadState]);

    function handleSubmitAll() {
        if (fileUploadParameters.uploadCategories?.length > 0) {
            selectedFiles.forEach(file => {
                var selectBox = document.getElementById(`${file.name}-upload-category-select-box`);
                file.selectedUploadCategory = selectBox.value;
            });
        }

        setFileUploadState(FileUploadState.Submit);
    }

    function addFile(e) {
        if (e.target.files.length === 0 || isTotalFileCountInvalid(e.target.files, selectedFiles)
            || isFileSizeInvalid(e.target.files) || isTotalFileSizeInvalid(e.target.files, selectedFiles)) {
            return;
        }

        const selectedFileNames = selectedFiles.map(file => file.name);
        const filteredFiles = [...e.target.files]
            .filter(file => !selectedFileNames.includes(file.name)
                && (fileUploadParameters?.allowInvalidFileTypes || fileUploadParameters?.allowedFileExtensions.some((extension) => {
                    return file.name.endsWith(extension);
                })));

        setSelectedFiles([...selectedFiles, ...filteredFiles]);
        e.target.value = "";
        setErrorMessage(null);
    }

    function dropEvent(e) {
        e.preventDefault();

        const droppedFiles = [...e.dataTransfer.items]
            .filter(item => item.kind === "file")
            .map(item => item.getAsFile());

        if (droppedFiles.length === 0 || isTotalFileCountInvalid(droppedFiles, selectedFiles)
            || isFileSizeInvalid(droppedFiles) || isTotalFileSizeInvalid(droppedFiles, selectedFiles)) {
            return;
        }

        const selectedFileNames = selectedFiles.map(file => file.name);
        const filteredFiles = droppedFiles
            .filter(file => !selectedFileNames.includes(file.name)
                && (fileUploadParameters?.allowInvalidFileTypes || fileUploadParameters?.allowedFileExtensions.some((extension) => {
                    return file.name.endsWith(extension);
                })));

        setSelectedFiles([...selectedFiles, ...filteredFiles]);
        e.target.value = "";
        setErrorMessage(null);
    }

    const removeFile = (selectedFile) => {
        setSelectedFiles(selectedFiles.filter(file => file !== selectedFile));
    };

    function uploadCategoryChanged(file, selectBoxId) {
        var selectBox = document.getElementById(selectBoxId);
        file.selectedUploadCategory = selectBox.value;
    }

    function isFileSizeInvalid(files) {
        return Array.from(files).some(file => {
            const fileSize = file.size / Math.pow(1024, 1);

            if (fileSize < fileUploadParameters?.minFileSize) {
                setErrorMessage(`${file.name} is less than the minimum size of ${formatBytes(fileUploadParameters.minFileSize)}. Please try again using a larger file.`);
                return true;
            }

            if (fileSize > fileUploadParameters?.maxFileSize) {

                setErrorMessage(`${file.name} is greater than the maximum size of ${formatBytes(fileUploadParameters.maxFileSize)}. Please try again using a smaller file.`);
                return true;
            }

            return false;
        })
    }

    function isTotalFileCountInvalid(files, uploadedFiles) {
        if (uploadedFiles.length === fileUploadParameters?.maxFileCount || uploadedFiles.length + files.length > fileUploadParameters?.maxFileCount) {
            setErrorMessage(`You can only submit up to ${fileUploadParameters?.maxFileCount} ${fileUploadParameters?.maxFileCount > 1 ? "files" : "file"} at a time.`);
            return true;
        }

        return false;
    }

    function isTotalFileSizeInvalid(files, uploadedFiles) {
        let combinedFileSize = 0;

        for (let index = 0; index < uploadedFiles.length; ++index) {
            combinedFileSize += uploadedFiles[index].size;
        }

        for (let index = 0; index < files.length; ++index) {
            combinedFileSize += files[index].size;
        }

        if ((combinedFileSize / Math.pow(1024, 1)) > fileUploadParameters?.maxCombinedFileSize) {
            setErrorMessage(`Your combined file sizes are greater than ${formatBytes(fileUploadParameters.maxCombinedFileSize)}. Please adjust your file uploads to a smaller combined size.`);
            return true;
        }

        return false;
    }

    return (
        <>
            {fileUploadState == FileUploadState.Error &&
                <div className="alert" id='file-upload-error-alert'>
                    <div className="alert--high-importance">
                        <span className="icon-alert"></span>
                        <ul className="alert-list">
                            <li className="alert-content">
                                <div className="h4">There was problem uploading your files.</div>
                                <p>Please try again.</p>
                            </li>
                        </ul>
                    </div>
                </div>
            }

            <div
                className={selectedFiles.length > 0 ? "drop doc-status-wrapper" : "drop drop-zone"}
                onDragOver={(e) => e.preventDefault()}
                multiple onDrop={dropEvent}
            >
                <div className="font--center">
                    <div className="p-1"><gds-icon icon-type='upload' /></div>
                    <p>Drop file here or
                        <span> </span>
                        <input
                            type="file"
                            name="file"
                            id="file"
                            className="hide-file"
                            multiple onChange={addFile}
                            accept={fileUploadParameters?.allowedFileExtensions}
                        />
                        <label htmlFor="file" className="drop-link">select file</label>
                    </p>
                </div>
                {selectedFiles.length > 0 &&
                    <div>
                        <div className="doc-upload container-padding">
                            <div className="doc-upload-header">
                                {displayParameters.uploadHeader}
                                {!displayParameters.bottomSubmitButton &&
                                    <button
                                        type="button"
                                        className="btn btn--primary btn--pull-right hidden-sm"
                                        onClick={handleSubmitAll}>
                                        <span>{displayParameters.submitButtonText}</span>
                                    </button>
                                }
                            </div>
                            {selectedFiles.map((file) => (
                                <div className={classes(!displayParameters.strokeSeparator && "margin-top")} key={`${file.name}-selected-file-container`}>
                                    {displayParameters.strokeSeparator && <span className="stroke-separator" />}
                                    <div className="doc-upload-files">
                                        <span className={`img-icon ${getFileExtensionIconClass(file.name)} hidden-sm hidden-md`} />
                                        <div>{file?.name}</div>
                                        <a>
                                            <span
                                                className="geico-icon geico-icon--actionable geico-icon--small icon-trash"
                                                onClick={() => removeFile(file, `${file.name}-selected-file-container`)}
                                            />
                                        </a>
                                    </div>
                                    {fileUploadParameters.uploadCategories?.length > 0 &&
                                        <div className="doc-upload-select">
                                            <div>
                                                <div className="form-field ">
                                                    <label
                                                        htmlFor="upload-category"
                                                        className="text ">
                                                        Select a category for this file:
                                                    </label>
                                                    <div className="select-box">
                                                        <select
                                                            id={`${file.name}-upload-category-select-box`}
                                                            name="upload-category"
                                                            onChange={() => { uploadCategoryChanged(file, `${file.name}-upload-category-select-box`) }}
                                                        >
                                                            {fileUploadParameters.uploadCategories.map(category =>
                                                                <option key={category.value} value={category.value}>{category.text}</option>
                                                            )}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </div>
                            ))}
                            {displayParameters.bottomSubmitButton &&
                                <div className='btn-center margin-top'>
                                    <button
                                        type="button"
                                        className="btn btn--primary mx-auto"
                                        onClick={handleSubmitAll}>
                                        <span>{displayParameters.submitButtonText}</span>
                                    </button>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        </>
    );
};

function formatBytes(kilobytes, decimals = 2) {
    if (!+kilobytes) return '0 KB'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(kilobytes) / Math.log(k))

    return `${parseFloat((kilobytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}
